import React, { useState, useEffect } from "react"
import { Modal, Input, Radio, Select, RadioChangeEvent } from "antd"
import { DefaultOptionType } from "antd/es/select"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"

import { Template } from "types/ReportingDashboard/reporting.types"
import { SelectOption } from "types/ui.types"

/**
 * Template action type for internal state management
 */
type ActionType = "create" | "replace"

/**
 * Template modal mode for different usage contexts
 */
type ModalMode = "create" | "edit"

/**
 * Save callback data structure
 */
export interface TemplateSaveData {
  name: string
  action: ActionType
}

/**
 * Props for TemplateModal component
 */
export interface TemplateModalProps {
  /** The title of the modal */
  title?: string
  /** Controls the visibility of the modal */
  isVisible: boolean
  /** Function to call when the cancel button is clicked */
  onCancel: () => void
  /** Function to call when the save button is clicked */
  onSave: (data: TemplateSaveData) => void
  /** Object containing existing templates (keyed by ID) */
  existingTemplates?: Record<string, Template>
  /** Default template name (usually empty) */
  defaultName?: string
  /** Mode of operation: 'create' or 'edit' */
  mode?: ModalMode
  /** ID of the currently selected template (for edit mode) */
  selectedTemplate?: string | null
}

/**
 * Template Modal Component
 *
 * A comprehensive modal for template management operations including:
 * - Creating new templates with custom names
 * - Replacing existing templates
 * - Template name validation and collision detection
 * - Dynamic UI based on available templates and mode
 * - Proper form validation and error handling
 *
 * @param props - Component props
 * @returns JSX element for template modal interface
 */
const TemplateModal: React.FC<TemplateModalProps> = ({
  title = "Save as Template",
  isVisible,
  onCancel,
  onSave,
  existingTemplates = {},
  defaultName = "",
  mode = "create", // 'create' when saving from slide, 'edit' when saving from template edit mode
  selectedTemplate, // ID of template being edited, or null
}) => {
  const [templateName, setTemplateName] = useState<string>(defaultName)
  const [actionType, setActionType] = useState<ActionType>("create") // Internal state: "create" or "replace"
  const [selectedExisting, setSelectedExisting] = useState<string | null>(null) // Holds ID of template selected in dropdown for replacement

  // Get template options for dropdown (memoization could be added if list is huge)
  const templateOptions: SelectOption[] = Object.keys(existingTemplates).map(
    (key: string) => ({
      value: key, // Use template ID as value
      label: existingTemplates[key].name, // Display template name
    })
  )

  // Reset state when modal opens/closes or relevant props change
  useEffect(() => {
    if (isVisible) {
      // Determine initial action type based on parent mode and if a template is selected
      const initialAction: ActionType =
        mode === "edit" && selectedTemplate && templateOptions.length > 0
          ? "replace"
          : "create"
      setActionType(initialAction)
      setSelectedExisting(selectedTemplate || null) // Set the initially selected template for replacement

      // Set initial name based on initial action type
      if (initialAction === "replace" && selectedTemplate) {
        // If starting in replace mode, prefill with the selected template's name
        setTemplateName(existingTemplates[selectedTemplate]?.name || "") // Use name, not ID
      } else {
        // If starting in create mode, use defaultName or ensure it's empty
        setTemplateName(defaultName || "")
      }
    } else {
      // Reset internal state when modal closes
      setTemplateName(defaultName || "")
      setActionType("create")
      setSelectedExisting(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, defaultName, mode, selectedTemplate, existingTemplates]) // Added existingTemplates dependency

  const handleSave = (): void => {
    // --- Validation ---
    if (actionType === "create" && !templateName.trim()) {
      notify.error("Template Error", "Please enter a template name.")
      return
    }
    if (actionType === "replace" && !selectedExisting) {
      notify.error(
        "Template Error",
        "Please select a template to replace."
      )
      return
    }

    // Check for name collisions when creating a *new* template
    // Note: Replacing an existing template with the same name is allowed.
    if (actionType === "create") {
      const nameExists = Object.values(existingTemplates).some(
        (template: Template) =>
          template.name.toLowerCase() === templateName.trim().toLowerCase()
      )
      if (nameExists) {
        notify.error(
          "Template Error",
          `A template named "${templateName.trim()}" already exists. Please choose a different name.`
        )
        return
      }
    }
    // --- End Validation ---

    // Call the parent save handler with appropriate params
    if (actionType === "create") {
      onSave({
        name: templateName.trim(), // Pass the entered name for the new template
        action: "create",
      })
    } else {
      // actionType === "replace"
      onSave({
        name: selectedExisting!, // Pass the ID of the template to replace (non-null assertion safe due to validation)
        action: "replace",
      })
    }
  }

  // Handle internal radio button change
  const handleActionTypeChange = (e: RadioChangeEvent): void => {
    const newActionType: ActionType = e.target.value
    setActionType(newActionType)

    // Update name field based on the NEW action type
    if (newActionType === "create") {
      // Clear name when switching to Create New
      setTemplateName("")
    } else if (newActionType === "replace") {
      // If switching back to Replace, try to refill with the selected template's name
      // If nothing is selected yet in dropdown, it will be empty
      setTemplateName(
        selectedExisting ? existingTemplates[selectedExisting]?.name || "" : ""
      )
    }
  }

  // Handle change in the replacement dropdown selection
  const handleSelectExistingChange = (value: string): void => {
    setSelectedExisting(value)
    // If currently in replace mode, update the name field to match selection
    if (actionType === "replace") {
      setTemplateName(value ? existingTemplates[value]?.name || "" : "")
    }
  }

  // Handle template name input change
  const handleTemplateNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setTemplateName(e.target.value)
  }

  return (
    <Modal
      centered
      title={title}
      open={isVisible}
      closable={false} // Controlled by Cancel button
      footer={null} // Custom footer buttons
      destroyOnHidden={true} // Reset state fully when modal is closed
    >
      <div className="flex flex-col mb-3">
        <p className="text-md mb-4">
          {/* Dynamic description based on mode */}
          {mode === "edit"
            ? "Save changes to the current template, replace another, or save as new."
            : "Save the current slide layout as a new template or replace an existing one."}
        </p>

        {/* Template Action Selection */}
        <div className="mb-4">
          <Radio.Group
            value={actionType}
            onChange={handleActionTypeChange} // Use new handler
          >
            <Radio value="create">Create New Template</Radio>
            {/* Only show Replace option if there are existing templates */}
            {templateOptions.length > 0 && (
              <Radio value="replace">Replace Existing Template</Radio>
            )}
          </Radio.Group>
        </div>

        {/* Template Name Input (for create) */}
        {actionType === "create" && (
          <div className="mb-4">
            <label htmlFor="template-name-input" className="block text-sm mb-1">
              Template Name <span className="text-red-500">*</span>
            </label>
            <Input
              id="template-name-input"
              value={templateName} // Controlled by state
              onChange={handleTemplateNameChange}
              placeholder="Enter template name"
              autoFocus // Focus when create is selected
            />
          </div>
        )}

        {/* Template Selection Dropdown (for replace) */}
        {actionType === "replace" && templateOptions.length > 0 && (
          <div className="mb-4">
            <label
              htmlFor="template-select-replace"
              className="block text-sm mb-1"
            >
              Select Template to Replace <span className="text-red-500">*</span>
            </label>
            <Select<string>
              id="template-select-replace"
              style={{ width: "100%" }}
              placeholder="Select a template..."
              value={selectedExisting} // Controlled by state
              onChange={handleSelectExistingChange} // Use specific handler
              options={templateOptions}
              // Allow searching template names
              showSearch
              filterOption={(input: string, option?: DefaultOptionType) =>
                String(option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </div>
        )}
      </div>

      {/* Footer Buttons */}
      <div className="flex flex-row space-x-2 justify-end">
        {" "}
        {/* Align buttons right */}
        <DataQButton type="secondary" onClick={onCancel}>
          Cancel
        </DataQButton>
        <DataQButton
          type="primary"
          onClick={handleSave}
          // Disable logic based on action type and valid selections/input
          isDisabled={
            (actionType === "create" && !templateName.trim()) ||
            (actionType === "replace" && !selectedExisting)
          }
        >
          {/* Dynamic button text */}
          {actionType === "create" ? "Save New Template" : "Replace Template"}
        </DataQButton>
      </div>
    </Modal>
  )
}

export default TemplateModal
